import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaUpload, FaRupeeSign, FaCalendar, FaInfoCircle, FaArrowRight, FaBuilding, FaCopy, FaShieldAlt } from 'react-icons/fa';
import { createInvestment } from '../../store/slices/investmentSlice';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';

const InvestmentPayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const selectedPlanFromState = location.state?.selectedPlan;

  const [formData, setFormData] = useState({
    amount: '',
    plan: selectedPlanFromState?.id || 'basic',
    uid: '',
    screenshot: null,
    maturityDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  // ============ 3 PLANS: Basic, Standard, Premium ============
  const plans = {
    basic: { min: 10000, max: 999999, rate: 60, name: 'Basic Plan', duration: 1 },
    standard: { min: 1000000, max: 1999999, rate: 66, name: 'Standard Plan', duration: 1 },
    premium: { min: 2000000, max: null, rate: 72, name: 'Premium Plan', duration: 2 }
  };

  const bankDetails = {
    bankName: 'Bank of India',
    accountName: 'Assets Dairy Farm',
    accountNumber: '750818210004206',
    ifsc: 'BKID0007508',
    upiId: 'assetsdairyfarm@bankofindia',
    branch: 'Barabanki, Uttar Pradesh',
    custId: '315868393',
    referenceNo: '11801256286',
    requestId: 'REQ 533314150914'
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  useEffect(() => {
    const selectedPlan = plans[formData.plan];
    const maturityDate = new Date();
    maturityDate.setMonth(maturityDate.getMonth() + (selectedPlan.duration * 12));
    setFormData(prev => ({ ...prev, maturityDate: maturityDate.toISOString().split('T')[0] }));
  }, [formData.plan]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setFormData({ ...formData, screenshot: file });
      setPreview(URL.createObjectURL(file));
    }
  };

  const calculateReturns = () => {
    const amount = parseFloat(formData.amount) || 0;
    const selectedPlan = plans[formData.plan];
    const yearlyReturn = amount * (selectedPlan.rate / 100);
    const totalReturn = yearlyReturn * selectedPlan.duration;
    return { yearly: yearlyReturn, total: totalReturn, monthly: yearlyReturn / 12 };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const selectedPlan = plans[formData.plan];
    const amountNum = parseFloat(formData.amount);
    
    if (!selectedPlan) {
      toast.error('Invalid plan selected');
      return;
    }
    if (amountNum < selectedPlan.min) {
      toast.error(`Minimum investment for ${selectedPlan.name} is ₹${selectedPlan.min.toLocaleString()}`);
      return;
    }
    if (selectedPlan.max && amountNum > selectedPlan.max) {
      toast.error(`Maximum investment for ${selectedPlan.name} is ₹${selectedPlan.max.toLocaleString()}`);
      return;
    }
    if (!formData.screenshot) {
      toast.error('Please upload payment screenshot');
      return;
    }
    if (!formData.uid) {
      toast.error('Please enter transaction UID');
      return;
    }

    setLoading(true);
    const data = new FormData();
    data.append('amount', formData.amount);
    data.append('plan', formData.plan);
    data.append('uid', formData.uid);
    data.append('maturityDate', formData.maturityDate);
    data.append('screenshot', formData.screenshot);

    try {
      await dispatch(createInvestment(data)).unwrap();
      navigate('/investor/my-investments');
    } catch (error) {
      console.error('Investment failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const { yearly, total, monthly } = calculateReturns();
  const selectedPlan = plans[formData.plan];

  return (
    <>
      <Helmet>
        <title>Make Investment - Assets Dairy Farm</title>
      </Helmet>

      <div className="bg-gradient-to-br from-green-50 to-blue-50 min-h-screen py-8 px-4">
        <div className="container mx-auto max-w-6xl">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-block bg-green-100 rounded-full px-5 py-1 mb-3">
              <span className="text-green-700 font-semibold text-sm">💰 Up to 72% Returns Guaranteed</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Complete Your Investment</h1>
            <p className="text-gray-500">Fill the details below to start your investment journey</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Form - Left Side */}
            <div className="md:col-span-2 bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold mb-5">Investment Details</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Plan Selection - 3 Plans */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm">Select Plan</label>
                  <div className="grid grid-cols-3 gap-3">
                    {Object.entries(plans).map(([key, plan]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setFormData({ ...formData, plan: key })}
                        className={`p-3 rounded-lg border-2 transition-all ${formData.plan === key ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'}`}
                      >
                        <div className="font-semibold text-sm">{plan.name}</div>
                        <div className="text-xs text-gray-500">₹{plan.min.toLocaleString()}+</div>
                        <div className="text-sm font-bold text-green-600 mt-1">{plan.rate}%</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm">Investment Amount (₹)</label>
                  <div className="relative">
                    <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                    <input type="number" name="amount" value={formData.amount} onChange={handleChange} required min={selectedPlan.min} max={selectedPlan.max || ''} className="w-full pl-8 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 text-sm" placeholder="Enter amount" />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Min: ₹{selectedPlan.min.toLocaleString()} {selectedPlan.max ? `| Max: ₹${selectedPlan.max.toLocaleString()}` : '| No upper limit'}</p>
                </div>

                {/* Maturity Date */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm">Maturity Date</label>
                  <div className="relative">
                    <FaCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                    <input type="date" name="maturityDate" value={formData.maturityDate} onChange={handleChange} required className="w-full pl-8 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
                  </div>
                </div>

                {/* UID */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm">Transaction UID / Reference Number</label>
                  <input type="text" name="uid" value={formData.uid} onChange={handleChange} required placeholder="Enter transaction ID from your bank" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
                </div>

                {/* Screenshot Upload */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm">Payment Screenshot</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-green-500 transition" onClick={() => document.getElementById('screenshot').click()}>
                    <FaUpload className="mx-auto h-8 w-8 text-gray-400 mb-1" />
                    <p className="text-gray-500 text-sm">Click to upload payment screenshot</p>
                    <p className="text-xs text-gray-400">PNG, JPG up to 5MB</p>
                  </div>
                  <input id="screenshot" type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                  {preview && <img src={preview} alt="Preview" className="mt-2 h-20 object-cover rounded-lg" />}
                </div>

                <button type="submit" disabled={loading} className="w-full bg-green-600 text-white py-2.5 rounded-lg font-semibold hover:bg-green-700 transition flex items-center justify-center gap-2 text-sm">
                  {loading ? 'Processing...' : 'Submit Investment'} <FaArrowRight />
                </button>
              </form>
            </div>

            {/* Info Panel - Right Side */}
            <div className="space-y-4">
              {/* Returns Calculator */}
              <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl shadow-lg p-4">
                <h3 className="text-base font-bold mb-3">Returns Calculator</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="opacity-90">Yearly Returns</span><span className="font-bold">₹{yearly.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="opacity-90">Monthly Returns</span><span className="font-bold">₹{monthly.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="opacity-90">Total Returns ({selectedPlan.duration} Year{selectedPlan.duration > 1 ? 's' : ''})</span><span className="font-bold">₹{total.toFixed(2)}</span></div>
                  <div className="pt-2 border-t border-white/20 flex justify-between"><span className="opacity-90">Interest Rate</span><span className="font-bold">{selectedPlan.rate}% p.a.</span></div>
                </div>
              </div>

              {/* Bank Details - PROPERLY ALIGNED */}
              <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
                <h3 className="text-base font-bold mb-3 flex items-center gap-2">
                  <FaBuilding className="text-green-600" /> Transfer to this Account
                </h3>
                <div className="space-y-2 text-sm">
                  {/* Bank Name */}
                  <div className="flex justify-between items-center bg-gray-50 p-2 rounded-lg">
                    <span className="text-gray-600 font-medium">Bank Name:</span>
                    <span className="font-semibold text-gray-800">{bankDetails.bankName}</span>
                  </div>
                  
                  {/* Account Name */}
                  <div className="flex justify-between items-center bg-gray-50 p-2 rounded-lg">
                    <span className="text-gray-600 font-medium">Account Name:</span>
                    <span className="font-semibold text-gray-800">{bankDetails.accountName}</span>
                  </div>
                  
                  {/* Account Number with Copy */}
                  <div className="flex justify-between items-center bg-gray-50 p-2 rounded-lg">
                    <span className="text-gray-600 font-medium">Account Number:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-green-600">{bankDetails.accountNumber}</span>
                      <button onClick={() => copyToClipboard(bankDetails.accountNumber)} className="text-blue-500 hover:text-blue-700 text-xs flex items-center gap-1">
                        <FaCopy size={10} /> Copy
                      </button>
                    </div>
                  </div>
                  
                  {/* IFSC with Copy */}
                  <div className="flex justify-between items-center bg-gray-50 p-2 rounded-lg">
                    <span className="text-gray-600 font-medium">IFSC Code:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-800">{bankDetails.ifsc}</span>
                      <button onClick={() => copyToClipboard(bankDetails.ifsc)} className="text-blue-500 hover:text-blue-700 text-xs flex items-center gap-1">
                        <FaCopy size={10} /> Copy
                      </button>
                    </div>
                  </div>
                  
                  {/* UPI with Copy */}
                  <div className="flex justify-between items-center bg-gray-50 p-2 rounded-lg">
                    <span className="text-gray-600 font-medium">UPI ID:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-800">{bankDetails.upiId}</span>
                      <button onClick={() => copyToClipboard(bankDetails.upiId)} className="text-blue-500 hover:text-blue-700 text-xs flex items-center gap-1">
                        <FaCopy size={10} /> Copy
                      </button>
                    </div>
                  </div>
                  
                  {/* Customer ID */}
                  <div className="flex justify-between items-center bg-gray-50 p-2 rounded-lg">
                    <span className="text-gray-600 font-medium">Customer ID:</span>
                    <span className="font-semibold text-gray-800">{bankDetails.custId}</span>
                  </div>
                  
                  {/* Reference No */}
                  <div className="flex justify-between items-center bg-gray-50 p-2 rounded-lg">
                    <span className="text-gray-600 font-medium">Reference No:</span>
                    <span className="font-semibold text-gray-800">{bankDetails.referenceNo}</span>
                  </div>
                  
                  {/* Request ID */}
                  <div className="flex justify-between items-center bg-gray-50 p-2 rounded-lg">
                    <span className="text-gray-600 font-medium">Request ID:</span>
                    <span className="font-semibold text-gray-800">{bankDetails.requestId}</span>
                  </div>
                  
                  {/* Branch */}
                  <div className="flex justify-between items-center bg-gray-50 p-2 rounded-lg">
                    <span className="text-gray-600 font-medium">Branch:</span>
                    <span className="font-semibold text-gray-800">{bankDetails.branch}</span>
                  </div>
                </div>
                
                {/* Warning Message */}
                <div className="mt-3 p-2 bg-yellow-50 rounded-lg text-xs text-yellow-800 text-center">
                  ⚠️ After payment, upload screenshot and enter UID above
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-white rounded-xl shadow-lg p-4">
                <h3 className="text-base font-bold mb-3 flex items-center gap-2"><FaInfoCircle className="text-green-600" /> Instructions</h3>
                <ol className="space-y-1 text-xs text-gray-600 list-decimal list-inside">
                  <li>Select your investment plan</li>
                  <li>Transfer amount to the bank account above</li>
                  <li>Take screenshot of successful transaction</li>
                  <li>Upload screenshot & enter UID</li>
                  <li>Get verified within 24 hours</li>
                  <li>Start earning {selectedPlan.rate}% returns!</li>
                </ol>
              </div>

              {/* Security Badge */}
              <div className="bg-green-50 rounded-lg p-3 text-center border border-green-200">
                <FaShieldAlt className="text-green-600 text-lg mx-auto mb-1" />
                <p className="text-xs text-gray-700">🔒 100% Secure Investment</p>
                <p className="text-xs text-gray-500">Your capital is fully protected</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InvestmentPayment;